import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Snackbar, TextField } from '@mui/material';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import SearchRounded from '@mui/icons-material/SearchRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import AddShoppingCartRounded from '@mui/icons-material/AddShoppingCartRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import axios, { portalRole } from '../services/api';
import { money } from '../services/swil';
import { buildIndex, csvLines, autoMatch, suggestions, searchProducts } from '../services/csvMatch';
import { getBackgroundColor, SchemeRibbon, ProductFormIcon } from './OrderParts';
import './OrderPages.css';
import './CsvOrder.css';

const SAMPLE_CSV = 'Product name,Qty\nCALPOL 250 MG SYP 60 ML,2\nDOLO 650 TAB 15 TAB,5\nBEXOL EYE DROP 5 ML,1\n';
const stockOf = (product) => Number(product?.total_stock_qty ?? product?.stock ?? 0) || 0;
const sourceLabel = { saved: 'Saved match', exact: 'Exact match', picked: 'Your match' };

function readJson(key, fallback) {
  try { const value = JSON.parse(localStorage.getItem(key)); return value ?? fallback; } catch { return fallback; }
}
function writeJson(key, value) {
  try { if (value == null) localStorage.removeItem(key); else localStorage.setItem(key, JSON.stringify(value)); } catch { /* Storage full or blocked: matches are not remembered. */ }
}

export default function CsvOrder({ customer, products, setProducts, cartItems = [], onAddToCart }) {
  const navigate = useNavigate();
  const customerId = customer?.customer_id ? String(customer.customer_id) : '';
  const matchesKey = `csv_matches_${customerId}`;
  const draftKey = `csv_order_draft_${customerId}`;
  const [catalogError, setCatalogError] = useState('');
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(null);
  const [draftOffer, setDraftOffer] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [searchText, setSearchText] = useState({});
  const [toast, setToast] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);
  const runId = useRef(0);
  // Matches shared across devices (server); null until loaded. 'device' = server unavailable, device copy only.
  const server = useRef({ matches: {}, loaded: null });
  const [matchStore, setMatchStore] = useState('loading');

  const index = useMemo(() => (products?.length ? buildIndex(products) : null), [products]);
  const product = (id) => index?.byId.get(String(id));

  // Load the catalogue if the product page has not loaded it yet.
  useEffect(() => {
    if (products?.length) return;
    let alive = true;
    axios.get('app_product_api.php').then(({ data }) => { if (alive) setProducts(data); })
      .catch((error) => { if (alive) setCatalogError(error.message || 'Products could not be loaded.'); });
    return () => { alive = false; };
  }, [products, setProducts]);

  // Load this customer's shared matches; fall back to the device copy if the server cannot provide them.
  useEffect(() => {
    if (!customerId) return undefined;
    let alive = true;
    server.current = { matches: {}, loaded: null };
    server.current.loaded = axios.get('csv_matches.php', { params: { customer_id: customerId } })
      .then(({ data }) => { if (alive) { server.current.matches = { ...(data.matches || {}) }; setMatchStore('server'); } })
      .catch(() => { if (alive) setMatchStore('device'); });
    return () => { alive = false; };
  }, [customerId]);

  // Offer an unfinished CSV order saved on this device.
  useEffect(() => {
    if (customerId && readJson(draftKey, null)?.rows?.length) setDraftOffer(true);
  }, [customerId, draftKey]);

  // Keep the unfinished order on this device until it is added to the cart or replaced.
  useEffect(() => {
    if (!customerId || progress) return;
    writeJson(draftKey, rows.length ? { savedAt: Date.now(), rows: rows.map(({ key, csvProduct, qty, productId, source }) => ({ key, csvProduct, qty, productId, source })) } : null);
  }, [rows, progress, customerId, draftKey]);

  if (!customer) {
    return <div className="order-page"><div className="order-empty">{portalRole === 'salesman' ? <><strong>No customer selected</strong>Select a customer from My Dashboard first, then upload their CSV.</> : <><strong>Loading your account…</strong>Please wait a moment.</>}</div></div>;
  }

  const matchInBatches = (lines) => {
    const id = ++runId.current;
    const saved = { ...readJson(matchesKey, {}), ...server.current.matches }; // shared matches win over this device's
    const pending = lines.map((line) => ({ ...line, productId: null, source: null, checking: true }));
    setRows(pending); setProgress({ done: 0, total: pending.length });
    let done = 0;
    const step = () => {
      if (id !== runId.current) return;
      const end = Math.min(done + 20, pending.length);
      for (; done < end; done += 1) {
        const match = autoMatch(pending[done], index, saved);
        pending[done] = { ...pending[done], checking: false, productId: match?.productId || null, source: match?.source || null };
      }
      setRows(sharedQuantities([...pending]));
      if (done < pending.length) { setProgress({ done, total: pending.length }); setTimeout(step, 0); }
      else { setProgress(null); setToast('Matching is ready. Review the products before adding them to the cart.'); }
    };
    setTimeout(step, 30);
  };

  const readFile = (file) => {
    if (!file) return;
    if (!index) { setToast(catalogError || 'Products are still loading. Please try again in a moment.'); return; }
    if (file.size > 5 * 1024 * 1024) { setToast('Please upload a CSV smaller than 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const lines = csvLines(event.target.result);
        setDraftOffer(false); setSearchText({});
        Promise.resolve(server.current.loaded).then(() => matchInBatches(lines));
      }
      catch (error) { setToast(error.message || 'This CSV could not be read.'); }
    };
    reader.onerror = () => setToast('The selected file could not be read.');
    reader.readAsText(file);
  };

  const resumeDraft = () => {
    const draft = readJson(draftKey, null);
    setDraftOffer(false);
    if (!draft?.rows?.length || !index) return;
    setRows(sharedQuantities(draft.rows.map((r) => ({ ...r, checking: false, productId: r.productId && index.byId.has(String(r.productId)) ? String(r.productId) : null }))));
    setToast('Resumed your saved CSV order.');
  };

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'csv-order-sample.csv' });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // Lines matched to the same catalogue product become one cart line, so they share one quantity.
  function sharedQuantities(list) {
    const most = {};
    list.forEach((r) => { if (r.productId) most[r.productId] = Math.max(most[r.productId] || 0, r.qty); });
    return list.map((r) => (r.productId && most[r.productId] !== r.qty ? { ...r, qty: most[r.productId] } : r));
  }
  const setMatch = (key, productId) => {
    setRows((current) => sharedQuantities(current.map((r) => (r.key === key ? { ...r, productId: productId ? String(productId) : null, source: productId ? 'picked' : null } : r))));
    const saved = readJson(matchesKey, {});
    if (productId) saved[key] = String(productId); else delete saved[key];
    writeJson(matchesKey, saved);
    const row = rows.find((r) => r.key === key);
    axios.post('csv_match.php', { customer_id: customerId, csv_product: row?.csvProduct || key, product_id: productId ? String(productId) : '' })
      .then(() => {
        if (productId) server.current.matches[key] = String(productId); else delete server.current.matches[key];
        setMatchStore('server');
        if (productId) setToast('Match saved for this customer. It will be used automatically next time, on any device.');
      })
      .catch(() => {
        setMatchStore('device');
        if (productId) setToast('Match saved on this device only. The server could not save it right now.');
      });
  };
  const pick = (row, productId) => {
    const others = rows.filter((r) => r.key !== row.key && r.productId === String(productId));
    if (others.length) setDuplicate({ row, productId: String(productId), others });
    else setMatch(row.key, productId);
  };
  const setQty = (row, qty) => {
    const next = Math.max(1, Math.floor(Number(qty) || 0));
    setRows((current) => current.map((r) => (r.key === row.key || (row.productId && r.productId === row.productId) ? { ...r, qty: next } : r)));
  };
  const removeRow = (row) => {
    if (!window.confirm(`Remove "${row.csvProduct}" from this order?`)) return;
    setRows((current) => current.filter((r) => r.key !== row.key));
  };

  const matched = rows.filter((r) => r.productId);
  const outOfStock = matched.filter((r) => stockOf(product(r.productId)) <= 0);
  const ready = matched.filter((r) => stockOf(product(r.productId)) > 0);
  const cartLines = [...new Map(ready.map((r) => [r.productId, r])).values()];
  const total = cartLines.reduce((sum, r) => sum + (Number(product(r.productId)?.ptr) || 0) * r.qty, 0);

  const addToCart = () => {
    cartLines.forEach((r) => {
      const p = product(r.productId);
      const existing = cartItems.find((item) => String(item.product_id) === String(p.product_id));
      onAddToCart({ ...p, quantity: (existing?.quantity || 0) + r.qty });
    });
    writeJson(draftKey, null); runId.current += 1; setRows([]);
    navigate('/search', { state: { csvAdded: cartLines.length } });
  };

  const byName = (a, b) => a.csvProduct.localeCompare(b.csvProduct, undefined, { numeric: true, sensitivity: 'base' });
  const sections = [
    { key: 'need', title: 'Need your match', list: rows.filter((r) => !r.productId).sort(byName) },
    { key: 'instock', title: 'In stock', list: ready.slice().sort(byName) },
    { key: 'oos', title: 'Out of stock (left out of the order)', list: outOfStock.slice().sort(byName) },
  ];
  const sharedWith = (row) => (row.productId ? rows.filter((r) => r.key !== row.key && r.productId === row.productId).length : 0);

  const matchedCard = (row) => {
    const p = product(row.productId);
    const stock = stockOf(p);
    return <div className="csv-match-card">
      <header style={{ backgroundColor: getBackgroundColor(stock) }}>
        <div><span className="csv-eyebrow">{sourceLabel[row.source] || 'Matched product'}</span><h4>{p.product_name}</h4></div>
        <SchemeRibbon scheme={p.scheme} />
      </header>
      <div className="csv-match-card__body">
        <ProductFormIcon name={p.product_name} />
        <dl>
          <div><dt>MRP</dt><dd>₹{p.mrp}</dd></div>
          <div><dt>PTR</dt><dd>{money(p.ptr)}</dd></div>
          <div><dt>Stock</dt><dd>{stock}</dd></div>
          <div><dt>GST</dt><dd>{p.gst}%</dd></div>
        </dl>
        <Button size="small" variant="outlined" startIcon={<EditRounded />} onClick={() => setMatch(row.key, null)}>Change</Button>
      </div>
      {stock > 0 && row.qty > stock && <p className="csv-warning">Only {stock} in stock. Orders above the available stock are refused, so lower the quantity.</p>}
      {sharedWith(row) > 0 && <p className="csv-note">Same product as {sharedWith(row)} other CSV line{sharedWith(row) === 1 ? '' : 's'}: added once, with one shared quantity.</p>}
    </div>;
  };

  const matchPicker = (row) => {
    const picks = suggestions(row.csvProduct, index);
    const typed = searchText[row.key] ?? row.csvProduct.split(/\s+/)[0];
    const results = typed.trim().length >= 2 ? searchProducts(typed, index, 40) : [];
    return <div className="csv-picker">
      {picks.length > 0 && <div className="csv-suggest">
        <span className="csv-eyebrow"><AutoAwesomeRounded fontSize="inherit" /> Suggested matches{picks[0].otherForm ? ' (no product in the same form, nearest shown)' : ''}</span>
        {picks.map(({ product: p, level }) => <button type="button" key={p.product_id} onClick={() => pick(row, p.product_id)}>
          <strong>{p.product_name}</strong><span className={`csv-conf csv-conf--${level.toLowerCase()}`}>{level}</span>
        </button>)}
      </div>}
      <TextField size="small" fullWidth label="Search the catalogue" value={typed} onChange={(e) => setSearchText((s) => ({ ...s, [row.key]: e.target.value }))}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} />
      {results.length > 0 && <div className="csv-results">{results.map((p) => <button type="button" key={p.product_id} onClick={() => pick(row, p.product_id)}>
        <span className="csv-dot" style={{ background: getBackgroundColor(stockOf(p)) }} /><strong>{p.product_name}</strong><small>Stock {stockOf(p)} · PTR {money(p.ptr)}</small>
      </button>)}</div>}
      {typed.trim().length >= 2 && !results.length && <p className="csv-note">No product starts with “{typed.trim()}”.</p>}
    </div>;
  };

  const rowCard = (row) => <article key={row.key} className={`csv-line ${row.productId ? '' : 'csv-line--need'}`}>
    <div className="csv-line__source">
      <span className="csv-eyebrow">CSV product</span>
      <strong>{row.csvProduct}</strong>
      {!progress && <button type="button" className="csv-remove" onClick={() => removeRow(row)}><DeleteOutlineRounded fontSize="inherit" /> Remove</button>}
    </div>
    <div className="csv-line__match">{row.checking ? <p className="csv-note">Checking…</p> : row.productId ? matchedCard(row) : matchPicker(row)}</div>
    <div className="csv-line__qty">
      <span className="csv-eyebrow">Quantity</span>
      <div className="qty-box">
        <IconButton size="small" onClick={() => setQty(row, row.qty - 1)} aria-label="Decrease quantity">−</IconButton>
        <input value={row.qty} inputMode="numeric" onChange={(e) => setQty(row, e.target.value)} aria-label={`Quantity for ${row.csvProduct}`} />
        <IconButton size="small" onClick={() => setQty(row, row.qty + 1)} aria-label="Increase quantity">+</IconButton>
      </div>
      {row.productId && stockOf(product(row.productId)) > 0 && <span className="csv-amount">{money((Number(product(row.productId)?.ptr) || 0) * row.qty)}</span>}
    </div>
  </article>;

  return <div className="order-page csv-order">
    <section className="csv-hero">
      <span className="csv-eyebrow">CSV order</span>
      <h2>Turn a product list into an order</h2>
      <p>Upload a two-column CSV for <strong>{customer.customer_name}</strong>, check the matches, then add everything to the normal cart in one step.</p>
      <Button component={Link} to="/search" size="small" startIcon={<ArrowBackRounded />}>Back to normal order</Button>
    </section>

    {catalogError && <div className="order-empty"><strong>Products could not be loaded</strong>{catalogError}</div>}

    <section className="csv-upload">
      <label className={`csv-drop ${dragging ? 'is-dragging' : ''}`} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); readFile(e.dataTransfer.files?.[0]); }}>
        <UploadFileRounded />
        <strong>{index ? 'Choose a CSV file' : 'Loading products…'}</strong>
        <span>Drop it here or tap to browse (up to 5 MB)</span>
        <input ref={fileInput} type="file" accept=".csv,text/csv" disabled={!index} onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      <aside className="csv-format">
        <h3>CSV format</h3>
        <p>The first row is the heading. Column 1 is the product name, column 2 the quantity. A product written twice is added together.</p>
        <table><tbody><tr><th>Product name</th><th>Qty</th></tr><tr><td>CALPOL 250 MG SYP 60 ML</td><td>2</td></tr></tbody></table>
        <Button size="small" startIcon={<FileDownloadOutlined />} onClick={downloadSample}>Download sample CSV</Button>
        <p className="csv-store">{matchStore === 'server' ? 'Your product matches are saved for this customer and shared across devices.' : matchStore === 'device' ? 'Product matches are saved on this device only.' : 'Loading saved matches…'}</p>
      </aside>
    </section>

    {progress && <section className="csv-progress"><strong>Matching products… {progress.done} / {progress.total}</strong><LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} /></section>}

    {rows.length > 0 && <>
      <section className="csv-stats">
        <div><span>Products in CSV</span><strong>{rows.length}</strong></div>
        <div className="is-good"><span>Matched</span><strong>{matched.length}</strong></div>
        <div className="is-need"><span>Need your match</span><strong>{rows.length - matched.length}</strong></div>
        {outOfStock.length > 0 && <div className="is-oos"><span>Out of stock</span><strong>{outOfStock.length}</strong></div>}
      </section>
      {sections.filter((s) => s.list.length).map((section) => <section key={section.key} className={`csv-section csv-section--${section.key}`}>
        <h3>{section.title}<span>{section.list.length}</span></h3>
        {section.list.map(rowCard)}
        {section.key === 'instock' && <div className="csv-total"><span>Total value (PTR, before scheme and GST)</span><strong>{money(total)}</strong></div>}
      </section>)}
      <div className="csv-footer">
        <p><strong>{progress ? 'Matching in progress…' : rows.length - matched.length ? `${rows.length - matched.length} product${rows.length - matched.length === 1 ? '' : 's'} still need a match` : `All ${rows.length} products are matched`}</strong>
          {cartLines.length ? `${cartLines.length} product${cartLines.length === 1 ? '' : 's'} ready to add.` : 'Match at least one in-stock product to continue.'}{outOfStock.length ? ` ${outOfStock.length} out of stock will be left out.` : ''}</p>
        <Button variant="contained" startIcon={<AddShoppingCartRounded />} disabled={Boolean(progress) || !cartLines.length} onClick={addToCart}>
          {cartLines.length ? `Add ${cartLines.length} product${cartLines.length === 1 ? '' : 's'} to cart` : 'Add to cart'}
        </Button>
      </div>
    </>}

    <Dialog open={draftOffer && Boolean(index)} onClose={() => setDraftOffer(false)}>
      <DialogTitle>Unfinished CSV order found</DialogTitle>
      <DialogContent>You have a CSV order for this customer saved on this device. Continue it, or start a new upload?</DialogContent>
      <DialogActions><Button onClick={() => { writeJson(draftKey, null); setDraftOffer(false); }}>Start fresh</Button><Button variant="contained" onClick={resumeDraft}>Continue saved order</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(duplicate)} onClose={() => setDuplicate(null)}>
      <DialogTitle>Use one product for two CSV lines?</DialogTitle>
      <DialogContent>
        <p className="csv-dialog-text">“{duplicate?.row.csvProduct}” and {duplicate?.others.map((r) => `“${r.csvProduct}”`).join(', ')} will both use <strong>{product(duplicate?.productId)?.product_name}</strong>. They become one cart line with one shared quantity.</p>
      </DialogContent>
      <DialogActions><Button onClick={() => setDuplicate(null)}>Choose another product</Button><Button variant="contained" onClick={() => { setMatch(duplicate.row.key, duplicate.productId); setDuplicate(null); }}>Yes, use this product</Button></DialogActions>
    </Dialog>

    <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
  </div>;
}
